import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatRut, formatDate, apiError, validateRut } from "../../lib/format";
import { toast } from "sonner";
import { Plus, Trash, Lock, LockOpen, PencilSimple, MagnifyingGlass, KeyReturn, X, UserCircle } from "@phosphor-icons/react";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pinReset, setPinReset] = useState(null);

  const load = () => api.get("/users", { params: { q, role: roleFilter || undefined } }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, [q, roleFilter]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { status: u.status === "active" ? "blocked" : "active" });
      toast.success(u.status === "active" ? "Cuenta bloqueada" : "Cuenta desbloqueada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Rol cambiado a ${role}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-mono mb-2">Administración</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Gestión de usuarios</h1>
        </div>
        <button onClick={() => setCreating(true)} className="h-11 px-5 bg-forest text-bg text-sm font-semibold flex items-center gap-2" data-testid="new-user-btn">
          <Plus size={14} weight="bold" /> Nuevo usuario
        </button>
      </header>

      <div className="bento p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 flex-1">
          <MagnifyingGlass size={18} weight="duotone" className="text-inkMuted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="flex-1 h-10 bg-transparent focus:outline-none" data-testid="user-search" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-10 border border-hairline px-3 text-sm" data-testid="role-filter">
          <option value="">Todos los roles</option>
          <option value="client">Clientes</option>
          <option value="advisor">Asesores</option>
          <option value="admin">Administradores</option>
        </select>
      </div>

      <div className="bento overflow-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="label-mono border-b border-hairline">
              <th className="text-left px-6 py-3">Usuario</th>
              <th className="text-left px-6 py-3">RUT</th>
              <th className="text-left px-6 py-3">Rol</th>
              <th className="text-left px-6 py-3">Estado</th>
              <th className="text-left px-6 py-3">Alta</th>
              <th className="text-right px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-hairline hover:bg-bg">
                <td className="px-6 py-3">
                  <div className="font-semibold">{u.name}</div>
                  <div className="label-mono">{u.email}</div>
                </td>
                <td className="px-6 py-3 font-mono">{u.rut}</td>
                <td className="px-6 py-3">
                  <select value={u.role} onChange={(e) => setRole(u, e.target.value)} disabled={u.id === me?.id} className="h-8 border border-hairline px-2 text-xs bg-surface" data-testid={`role-${u.id}`}>
                    <option value="client">client</option>
                    <option value="advisor">advisor</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase tracking-wider ${u.status === "active" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                    {u.status}
                  </span>
                </td>
                <td className="px-6 py-3 font-mono text-xs text-inkMuted">{formatDate(u.created_at)}</td>
                <td className="px-6 py-3 text-right space-x-1">
                  <button onClick={() => setEditing(u)} className="p-2 border border-hairline hover:bg-forest hover:text-bg" title="Editar" data-testid={`edit-${u.id}`}><PencilSimple size={14} weight="bold" /></button>
                  <button onClick={() => setPinReset(u)} className="p-2 border border-hairline hover:bg-burnt hover:text-bg hover:border-burnt" title="Resetear PIN" data-testid={`pin-${u.id}`}><KeyReturn size={14} weight="bold" /></button>
                  <button onClick={() => toggleBlock(u)} className="p-2 border border-hairline hover:bg-warn hover:text-bg hover:border-warn" title="Bloquear/Desbloquear" data-testid={`block-${u.id}`}>
                    {u.status === "active" ? <Lock size={14} weight="bold" /> : <LockOpen size={14} weight="bold" />}
                  </button>
                  <button onClick={() => remove(u.id)} disabled={u.id === me?.id} className="p-2 border border-danger text-danger hover:bg-danger hover:text-bg disabled:opacity-30" title="Eliminar" data-testid={`delete-${u.id}`}><Trash size={14} weight="bold" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreateUserModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {pinReset && <PinResetModal user={pinReset} onClose={() => setPinReset(null)} />}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface border border-hairline max-w-lg w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><UserCircle size={20} weight="duotone" />{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-bg"><X size={16} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", rut: "", phone: "+56 ", email: "", pin: "", role: "client" });
  const submit = async (e) => {
    e.preventDefault();
    if (!validateRut(form.rut)) return toast.error("RUT inválido");
    if (form.pin.length !== 4) return toast.error("PIN de 4 dígitos");
    try {
      await api.post("/users", { ...form, rut: formatRut(form.rut) });
      toast.success("Usuario creado");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal title="Nuevo usuario" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <F label="Nombre"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="new-name" /></F>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="RUT"><input value={form.rut} onChange={(e) => setForm({ ...form, rut: formatRut(e.target.value) })} className="input font-mono" data-testid="new-rut" /></F>
          <F label="Teléfono"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="new-phone" /></F>
        </div>
        <F label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="new-email" /></F>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="PIN"><input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="input font-mono" data-testid="new-pin" /></F>
          <F label="Rol"><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input" data-testid="new-role"><option value="client">client</option><option value="advisor">advisor</option><option value="admin">admin</option></select></F>
        </div>
        <button className="h-11 px-5 bg-forest text-bg text-sm font-semibold w-full" data-testid="new-submit">Crear usuario</button>
        <style>{`.input { width: 100%; height: 44px; padding: 0 12px; border: 1px solid rgba(13,31,22,0.2); background: #fff; }`}</style>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ name: user.name, phone: user.phone, email: user.email });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/users/${user.id}`, form);
      toast.success("Usuario actualizado");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal title={`Editar ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <F label="Nombre"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></F>
        <F label="Teléfono"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></F>
        <F label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" /></F>
        <button className="h-11 px-5 bg-forest text-bg text-sm font-semibold w-full">Guardar</button>
        <style>{`.input { width: 100%; height: 44px; padding: 0 12px; border: 1px solid rgba(13,31,22,0.2); background: #fff; }`}</style>
      </form>
    </Modal>
  );
}

function PinResetModal({ user, onClose }) {
  const [pin, setPin] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return toast.error("PIN de 4 dígitos");
    try {
      await api.post(`/users/${user.id}/reset-pin`, { new_pin: pin });
      toast.success("PIN reseteado");
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal title={`Resetear PIN · ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <F label="Nuevo PIN (4 dígitos)"><input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} className="input font-mono text-2xl tracking-[0.5em]" data-testid="reset-pin-input" /></F>
        <button className="h-11 px-5 bg-burnt text-bg text-sm font-semibold w-full" data-testid="reset-pin-submit">Aplicar</button>
        <style>{`.input { width: 100%; height: 52px; padding: 0 12px; border: 1px solid rgba(13,31,22,0.2); background: #fff; }`}</style>
      </form>
    </Modal>
  );
}

function F({ label, children }) {
  return (<div><label className="label-mono block mb-1.5">{label}</label>{children}</div>);
}
